import React, { useState } from "react";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { useAuth } from "../auth/AuthContext";
import { postLoginTarget } from "../utils/loginRedirect";
import "./Login.css";

const EmailIcon = () => (
  <svg
    className="login-field-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="18"
    height="18"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="2" ry="2" />
    <polyline points="3,7 12,13 21,7" />
  </svg>
);

const LockIcon = () => (
  <svg
    className="login-field-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="18"
    height="18"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const sloganByMode = {
  login: "Sign in",
  reset: "Reset password",
  "reset-confirm": "Set new password",
  "new-password": "Set new password",
};

const Login = () => {
  const { login, completeNewPassword, user, loading } = useAuth();

  React.useEffect(() => {
    // Full browser navigation, not react-router's navigate(): a client-side
    // SPA transition from Login to Projects leaves the destination page
    // visibly broken on mobile (large layout gap, top nav not rendering
    // correctly) — a real page load reliably renders it correctly, and a
    // manual refresh already confirms this fixes it every time.
    // Goes to the page the visitor originally opened (?next=, validated —
    // utils/loginRedirect.js), else the dashboard.
    if (!loading && user) window.location.href = postLoginTarget();
  }, [user, loading]);

  const [mode, setMode] = useState("login"); // "login" | "reset" | "new-password"
  const [resetSent, setResetSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [resetCodeError, setResetCodeError] = useState(false);
  const [formError, setFormError] = useState("");

  // Handle autofill styling
  React.useEffect(() => {
    const handleAutofill = () => {
      const emailInputs = document.querySelectorAll('input[type="email"]');
      emailInputs.forEach((input) => {
        if (input.value) {
          // Force remove autofill background
          input.style.webkitBoxShadow = '0 0 0 1000px rgba(255, 255, 255, 0.03) inset';
          input.style.webkitTextFillColor = 'rgba(255, 255, 255, 0.9)';
        }
      });
    };

    // Check on mount and when email changes
    handleAutofill();
    
    // Also check after a slight delay to catch browser autofill
    const timer = setTimeout(handleAutofill, 100);
    
    return () => clearTimeout(timer);
  }, [email]);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const handleLogin = async (e) => {
    e.preventDefault();
    let hasError = false;
    if (!isValidEmail(email)) { setEmailError(true); hasError = true; }
    if (!password.trim()) { setPasswordError(true); hasError = true; }
    if (hasError) return;

    try {
      setFormError("");
      const result = await login(email, password);
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setPassword("");
        setMode("new-password");
        return;
      }
      // Full page load, not client-side navigate() — see effect above for why.
      window.location.href = postLoginTarget();
    } catch (err) {
      if (err?.name === "NetworkError" || !navigator.onLine) {
        setFormError("Network error. Please check your internet connection and try again.");
      } else {
        setFormError("Incorrect email or password.");
      }
    }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    let hasError = false;
    if (!newPassword.trim()) { setNewPasswordError(true); hasError = true; }
    if (!confirmPassword.trim()) { setConfirmPasswordError(true); hasError = true; }
    if (!hasError && newPassword !== confirmPassword) {
      setConfirmPasswordError(true);
      setFormError("Passwords do not match.");
      return;
    }
    if (hasError) return;

    try {
      setFormError("");
      await completeNewPassword(newPassword);
      // Full page load, not client-side navigate() — see effect above for why.
      window.location.href = postLoginTarget();
    } catch (err) {
      setFormError(err.message || "Failed to set new password.");
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) { setEmailError(true); return; }

    try {
      await resetPassword({ username: email });
    } catch (err) {
      if (err?.name === "LimitExceededException") {
        setFormError("Too many attempts. Please try again later.");
        return;
      }
      // Always proceed for other errors — don't reveal if email exists
    }
    setMode("reset-confirm");
  };

  const handleResetConfirm = async (e) => {
    e.preventDefault();
    let hasError = false;
    if (!resetCode.trim()) { setResetCodeError(true); hasError = true; }
    if (!newPassword.trim()) { setNewPasswordError(true); hasError = true; }
    if (!confirmPassword.trim()) { setConfirmPasswordError(true); hasError = true; }
    if (!hasError && newPassword !== confirmPassword) {
      setConfirmPasswordError(true);
      setFormError("Passwords do not match.");
      return;
    }
    if (hasError) return;

    try {
      setFormError("");
      await confirmResetPassword({ username: email, confirmationCode: resetCode, newPassword });
      setResetCode("");
      setNewPassword("");
      setConfirmPassword("");
      setMode("reset-success");
    } catch (err) {
      setFormError(err.message || "Invalid code or password.");
    }
  };

  const switchToReset = () => {
    setMode("reset");
    setResetSent(false);
    setEmail("");
    setPassword("");
    setEmailError(false);
    setPasswordError(false);
    setFormError("");
  };

  const switchToLogin = () => {
    setMode("login");
    setResetSent(false);
    setEmail("");
    setPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setEmailError(false);
    setPasswordError(false);
    setNewPasswordError(false);
    setConfirmPasswordError(false);
    setResetCode("");
    setResetCodeError(false);
    setFormError("");
  };

  const isNewPasswordMode = mode === "new-password";
  const isResetConfirmMode = mode === "reset-confirm";
  const isResetSuccessMode = mode === "reset-success";

  return (
    <div className="login-page">
      <div className="login-content">
        <img src="images/landing-logo.svg" alt="Cinesplat Logo" className="login-logo" />
        <div className="login-card">
          {isResetSuccessMode ? (
            <div className="reset-success">
              <div className="reset-success-badge">
                <svg
                  className="reset-success-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="4 12.5 9.5 18 20 6" />
                </svg>
              </div>
              <p className="reset-success-title">Password updated</p>
              <p className="reset-success-text">
                Your password has been changed successfully. You can now sign in with your new password.
              </p>
              <button type="button" className="login-button" onClick={switchToLogin}>
                Go to login
              </button>
            </div>
          ) : (
          <>
          <p className="login-slogan">{sloganByMode[mode]}</p>

          {/* Reset password success message */}
          {resetSent ? (
            <div className="reset-sent-message">
              <svg
                className="reset-sent-icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="9 12 11.5 14.5 15.5 9.5" />
              </svg>
              If this email address exists, we've sent you a password reset link.
            </div>
          ) : null}

          {/* Reset confirm form — code + new password */}
          {isResetConfirmMode && (
            <form onSubmit={handleResetConfirm} noValidate style={resetSent ? { display: "none" } : undefined}>
              <div className="login-field">
                <svg className="login-field-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true">
                  <rect x="2" y="7" width="20" height="14" rx="2" />
                  <path d="M16 3H8L2 7" />
                  <path d="M22 7l-6-4" />
                </svg>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => { setResetCode(e.target.value); setResetCodeError(false); }}
                  className={`login-input${resetCodeError ? " input-error" : ""}`}
                  placeholder="Verification code from email"
                  aria-label="Verification code"
                  autoComplete="one-time-code"
                />
              </div>
              <div className="login-field">
                <LockIcon />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setNewPasswordError(false); setFormError(""); }}
                  className={`login-input${newPasswordError ? " input-error" : ""}`}
                  placeholder="New password"
                  aria-label="New password"
                  autoComplete="new-password"
                />
              </div>
              <div className="login-field">
                <LockIcon />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setConfirmPasswordError(false); setFormError(""); }}
                  className={`login-input${confirmPasswordError ? " input-error" : ""}`}
                  placeholder="Confirm new password"
                  aria-label="Confirm new password"
                  autoComplete="new-password"
                />
              </div>
              {formError && <p className="login-error">{formError}</p>}
              <button type="submit" className="login-button">Set new password</button>
            </form>
          )}

          {/* Login / Reset form */}
          {!isNewPasswordMode && !isResetConfirmMode && (
            <form onSubmit={mode === "login" ? handleLogin : handleReset} noValidate style={resetSent ? { display: "none" } : undefined}>
              {/* Email field */}
              <div className="login-field">
                <EmailIcon />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(false);
                    // Only apply white text if there's a value, otherwise reset to default
                    if (e.target.value) {
                      e.target.style.webkitBoxShadow = '0 0 0 1000px rgba(255, 255, 255, 0.03) inset';
                      e.target.style.webkitTextFillColor = 'rgba(255, 255, 255, 0.9)';
                    } else {
                      e.target.style.webkitBoxShadow = '';
                      e.target.style.webkitTextFillColor = '';
                    }
                  }}
                  className={`login-input${emailError ? " input-error" : ""}`}
                  placeholder="Email address"
                  aria-label="Email address"
                  autoComplete="email"
                />
              </div>

              {/* Password field — login mode only */}
              <div className={`login-field-collapsible${mode === "login" ? " visible" : ""}`}>
                <div className="login-field">
                  <LockIcon />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPasswordError(false); }}
                    className={`login-input${passwordError ? " input-error" : ""}`}
                    placeholder="Password"
                    aria-label="Password"
                    autoComplete="current-password"
                    tabIndex={mode === "login" ? 0 : -1}
                  />
                </div>
              </div>

              {formError && <p className="login-error">{formError}</p>}

              <button type="submit" className="login-button">
                {mode === "login" ? "Sign in" : "Send reset link"}
              </button>
            </form>
          )}

          {/* New password form */}
          {isNewPasswordMode && (
            <form onSubmit={handleSetNewPassword} noValidate>
              <div className="login-field">
                <LockIcon />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setNewPasswordError(false); setFormError(""); }}
                  className={`login-input${newPasswordError ? " input-error" : ""}`}
                  placeholder="New password"
                  aria-label="New password"
                  autoComplete="new-password"
                />
              </div>
              <div className="login-field">
                <LockIcon />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setConfirmPasswordError(false); setFormError(""); }}
                  className={`login-input${confirmPasswordError ? " input-error" : ""}`}
                  placeholder="Confirm new password"
                  aria-label="Confirm new password"
                  autoComplete="new-password"
                />
              </div>

              {formError && <p className="login-error">{formError}</p>}

              <button type="submit" className="login-button">
                Set password
              </button>
            </form>
          )}

          {!isNewPasswordMode && !isResetConfirmMode && mode === "login" && (
            <>
              <button
                type="button"
                className="reset-password-button"
                onClick={switchToReset}
              >
                Forgot password?
              </button>
            </>
          )}
          {(!isNewPasswordMode && !isResetConfirmMode && mode !== "login") && (
            <div className="login-contact" style={{ marginTop: 14 }}>
              <span>Back to</span>
              <button type="button" className="login-contact-link" onClick={switchToLogin}>Sign in</button>
            </div>
          )}
          {isResetConfirmMode && (
            <div className="login-contact" style={{ marginTop: 14 }}>
              <span>Back to</span>
              <button type="button" className="login-contact-link" onClick={switchToLogin}>Sign in</button>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
